<template>
  <label
    class="file-upload"
    :class="{ highlighted: isDropAreaHighlighted, compact: false, disabled: disabled }"
    @dragenter.prevent="isDropAreaHighlighted = true"
    @dragover.prevent="isDropAreaHighlighted = true"
    @dragleave.prevent="isDropAreaHighlighted = false"
    @drop.prevent="onDropFile">
    <icon-complex-upload class="mr-3" />
    <span v-sanitize-html="message" />
    <input
      :id="id"
      ref="fileInput"
      type="file"
      :disabled="disabled"
      @change="onFileChange" />
  </label>
</template>

<script setup lang="ts">
  import { ref } from 'vue';

  import { IconComplexUpload } from '@/common/icons/complex';

  interface IProps {
    message?: string;
    id?: string;
    disabled?: boolean;
  }
  defineProps<IProps>();

  const isDropAreaHighlighted = ref(false);

  interface IEmits {
    (event: 'fileDrop', fileList: File[]): void;
  }

  const emits = defineEmits<IEmits>();

  function onDropFile(event: DragEvent) {
    isDropAreaHighlighted.value = false;
    const fileList = Array.from(event.dataTransfer.files);
    emits('fileDrop', fileList);
  }

  function onFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const fileList = Array.from(target.files);
    emits('fileDrop', fileList);
  }
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .file-upload {
    display: flex;
    align-items: center;
    border-style: dashed;
    border-width: 0.2rem;
    border-color: $borders;
    padding: 2rem 4.4rem;
    border-radius: 1.5rem;
    background: #00000005;
    cursor: pointer;
    transition: all 0.2s $bezier-ease-out;

    &:hover,
    &.highlighted {
      border-color: var(--actionsColor);
      background: var(--actionsColorFaded95);
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    svg {
      width: 12.8rem;
      height: 12.8rem;
      filter: grayscale(1);
    }

    input {
      display: none;
    }

    a {
      color: var(--actionsColor);
    }

    &.compact {
      padding: 1rem 2.2rem;
      font-size: 0.9em;

      svg {
        width: 6.4rem;
        height: 6.4rem;
      }
    }
  }
</style>
